import { round } from 'lodash';
import z from 'zod';

import { assertNever } from '../assertNever';
import { brandedId, PaymentBreakdownId, PaymentId } from '../BrandedIds';
import { AppendOnlyArray } from '../utils/AppendOnlyArray';
import { getUnvoidedBreakdowns, PaymentBreakdown } from './PaymentBreakdown';
import { Payment } from './Payments';

export class PaymentBreakdownArray extends AppendOnlyArray<PaymentBreakdown> {
  constructor(array: readonly PaymentBreakdown[]) {
    super(array);
  }

  public reallocatePayment(paymentId: PaymentId, now: Date, newBreakdowns: PaymentBreakdown[]): void {
    return this.reallocatePaymentWithOverriddenPaymentId(paymentId, paymentId, now, newBreakdowns);
  }

  /**
   * This supports our (slightly weird) behavior for reversals-to-suspense (in
   * which the voided breakdowns are attributed to the reversal payment _doing
   * the voiding and reallocating_ rather than to the payment _being_ voided)
   *
   * Because of that attribution, voidedness must be computed over the FULL
   * array before narrowing to this payment's breakdowns. A view pre-filtered
   * to `paymentId === paymentIdToReallocate` excludes the void markers a
   * prior reversal created (they carry the reversal's paymentId), so an
   * already-reversed breakdown would look live and get voided again —
   * crediting suspense a second time for money that was only received once.
   */
  public reallocatePaymentWithOverriddenPaymentId(
    paymentIdToReallocate: PaymentId,
    paymentIdToAttributeForVoidedBreakdowns: PaymentId,
    now: Date,
    newBreakdowns: PaymentBreakdown[],
  ): void {
    const toReallocate = getUnvoidedBreakdowns(this).filter((b) => b.paymentId === paymentIdToReallocate);
    const toPush: PaymentBreakdown[] = [];

    for (const breakdown of toReallocate) {
      toPush.push(createVoidedBreakdown(breakdown, { createdDate: now, id: paymentIdToAttributeForVoidedBreakdowns }));
    }

    toPush.push(...newBreakdowns);

    if (add(...toPush.map((b) => b.amount)) !== 0) {
      throw new Error(`Total amount of reallocated breakdowns does not match the total amount of the payment`);
    }

    this.push(...toPush);
  }

  public voidPayment(paymentId: PaymentId, now: Date, totalOfPaymentBeingVoided: number): void {
    return this.voidPaymentWithOverriddenPaymentId(paymentId, paymentId, now, totalOfPaymentBeingVoided);
  }

  /**
   * This supports our (slightly weird) behavior for reversals (in which the
   * voided breakdowns are attributed to the reversal payment _doing the voiding_
   * rather than to the payment _being_ voided)
   *
   * Voidedness is computed over the full array (not a view pre-filtered to
   * `paymentIdToVoid`) for the reason described on
   * {@link reallocatePaymentWithOverriddenPaymentId}: a prior reversal's void
   * markers carry a different paymentId, and missing them here would
   * double-void the same breakdown.
   */
  public voidPaymentWithOverriddenPaymentId(
    paymentIdToVoid: PaymentId,
    paymentIdToAttributeForVoidedBreakdowns: PaymentId,
    now: Date,
    totalOfPaymentBeingVoided: number,
  ): void {
    const toVoid = getUnvoidedBreakdowns(this).filter((b) => b.paymentId === paymentIdToVoid);
    const toPush: PaymentBreakdown[] = [];

    for (const breakdown of toVoid) {
      toPush.push(createVoidedBreakdown(breakdown, { createdDate: now, id: paymentIdToAttributeForVoidedBreakdowns }));
    }

    const totalVoidedAmounts = add(...toPush.map((b) => b.amount));
    if (totalVoidedAmounts !== -totalOfPaymentBeingVoided) {
      throw new Error(
        `Total amount of voided breakdowns does not match the total of the payment being voided (expected=${-totalOfPaymentBeingVoided}, actual=${totalVoidedAmounts})`,
      );
    }

    this.push(...toPush);
  }

  public voidBreakdown(paymentBreakdownId: PaymentBreakdownId, now: Date): void {
    const breakdown = this.find((b) => b.id === paymentBreakdownId);
    if (!breakdown) {
      throw new Error(`Breakdown with id ${paymentBreakdownId} not found`);
    }
    this.push(createVoidedBreakdown(breakdown, { createdDate: now, id: breakdown.paymentId }));
  }

  public reallocateBreakdown(
    paymentBreakdownId: PaymentBreakdownId,
    now: Date,
    newBreakdowns: PaymentBreakdown[],
  ): void {
    const breakdown = this.find((b) => b.id === paymentBreakdownId);
    if (!breakdown) {
      throw new Error(`Breakdown with id ${paymentBreakdownId} not found`);
    }

    const toPush: PaymentBreakdown[] = [];

    toPush.push(createVoidedBreakdown(breakdown, { createdDate: now, id: breakdown.paymentId }));
    toPush.push(...newBreakdowns);

    if (add(...toPush.map((b) => b.amount)) !== 0) {
      throw new Error(`Total amount of reallocated breakdowns does not match the total amount of the payment`);
    }

    this.push(...toPush);
  }

  public voidAllBreakdowns(now: Date): void {
    const toVoid = getUnvoidedBreakdowns(this);
    for (const breakdown of toVoid) {
      this.push(createVoidedBreakdown(breakdown, { createdDate: now, id: breakdown.paymentId }));
    }
  }
}

export function createVoidedBreakdown(
  breakdown: PaymentBreakdown,
  { createdDate: createdAt, id: paymentId }: Pick<Payment, 'createdDate' | 'id'>,
): Required<PaymentBreakdown> /* Required is here to make sure we don't miss an optional field */ {
  const baseBreakdown = {
    id: brandedId('PaymentBreakdownId'),
    createdAt,
    voidedBreakdownId: breakdown.id!,
    event: 'voided',
    paymentId,
    amount: negateUnlessZero(breakdown.amount),
    expectedPayment: breakdown.expectedPayment,
  } as const;

  switch (breakdown.type) {
    case 'statement':
      return {
        ...baseBreakdown,
        type: 'statement',
        paymentDueId: breakdown.paymentDueId,
        principal: negateUnlessZero(breakdown.principal),
        interest: negateUnlessZero(breakdown.interest),
        escrow: negateUnlessZero(breakdown.escrow),
        additionalPrincipal: negateUnlessZero(breakdown.additionalPrincipal),
        additionalEscrow: negateUnlessZero(breakdown.additionalEscrow),
        suspense: negateUnlessZero(breakdown.suspense),
        futureStatementSuspense: negateUnlessZero(breakdown.futureStatementSuspense),
        unappliedSuspense: negateUnlessZero(breakdown.unappliedSuspense),
        reserve: negateUnlessZero(breakdown.reserve ?? 0),
      };
    case 'suspense':
      return {
        ...baseBreakdown,
        type: 'suspense',
        futureStatementSuspense: negateUnlessZero(breakdown.futureStatementSuspense),
        unappliedSuspense: negateUnlessZero(breakdown.unappliedSuspense),
        paymentDueId: breakdown.paymentDueId!,
      };

    case 'fee':
      return {
        ...baseBreakdown,
        type: 'fee',
        feeId: breakdown.feeId,
        fees: negateUnlessZero(breakdown.fees),
        unappliedSuspense: negateUnlessZero(breakdown.unappliedSuspense),
      };
    case 'thirdParty':
      return {
        ...baseBreakdown,
        type: 'thirdParty',
        escrow: negateUnlessZero(breakdown.escrow),
        holdback: negateUnlessZero(breakdown.holdback),
        lossDraft: negateUnlessZero(breakdown.lossDraft),
        suspense: negateUnlessZero(breakdown.suspense ?? 0), // @migrationTodo
        unappliedSuspense: negateUnlessZero(breakdown.unappliedSuspense),
        principal: negateUnlessZero(breakdown.principal),
      };
    case 'advance':
      return {
        ...baseBreakdown,
        type: 'advance',
        feeId: breakdown.feeId,
        advance: negateUnlessZero(breakdown.advance),
        advanceInterest: negateUnlessZero(breakdown.advanceInterest),
        unappliedSuspense: negateUnlessZero(breakdown.unappliedSuspense),
      };
    case 'payoff':
      return {
        ...baseBreakdown,
        type: 'payoff',
        payoffId: breakdown.payoffId,
        principal: negateUnlessZero(breakdown.principal),
        additionalPrincipal: negateUnlessZero(breakdown.additionalPrincipal ?? 0),
        interest: negateUnlessZero(breakdown.interest),
        escrow: negateUnlessZero(breakdown.escrow),
        additionalEscrow: negateUnlessZero(breakdown.additionalEscrow ?? 0),
        suspense: negateUnlessZero(breakdown.suspense),
        futureStatementSuspense: negateUnlessZero(breakdown.futureStatementSuspense),
        unappliedSuspense: negateUnlessZero(breakdown.unappliedSuspense),
        reserve: negateUnlessZero(breakdown.reserve),
        fees: negateUnlessZero(breakdown.fees),
      };
    case 'balanceReallocation':
      return {
        ...baseBreakdown,
        type: 'balanceReallocation',
        escrow: negateUnlessZero(breakdown.escrow),
        holdback: negateUnlessZero(breakdown.holdback),
        lossDraft: negateUnlessZero(breakdown.lossDraft),
        suspense: negateUnlessZero(breakdown.suspense ?? 0), // @migrationTodo
        unappliedSuspense: negateUnlessZero(breakdown.unappliedSuspense),
        principal: negateUnlessZero(breakdown.principal),
        futureStatementSuspense: negateUnlessZero(breakdown.futureStatementSuspense),
        amount: negateUnlessZero(breakdown.amount),
      };
    case 'principalPrepayment':
      return {
        ...baseBreakdown,
        type: 'principalPrepayment',
        additionalPrincipal: negateUnlessZero(breakdown.additionalPrincipal ?? 0),
        reserve: negateUnlessZero(breakdown.reserve ?? 0),
      };
    default:
      return assertNever(breakdown);
  }
}

function negateUnlessZero(amount: number): number {
  return amount === 0 ? 0 : amount * -1;
}

// duplicated from shared-iso because dependency cycles
function add(...nums: number[]): number {
  return addPrecision(2, ...nums);
}
// duplicated from shared-iso because dependency cycles
function addPrecision(precision: number = 2, ...nums: number[]): number {
  let sum = 0;
  for (const num of nums) {
    sum += num;
  }
  return round(sum, precision);
}

export const zodPaymentBreakdownArray = z
  .preprocess(
    (items: unknown) => (items instanceof PaymentBreakdownArray ? items.toReadonlyArray() : items),
    z.array(PaymentBreakdown),
  )
  .transform((items) => new PaymentBreakdownArray(items));
